import { CalendarsListState } from '@modules/calendar-app/types/calendars-list-state';
import { EventsListState } from '@modules/calendar-app/types/events-list-state';
import { type ListStateService } from '@modules/common/components/list/list-state.service';
import { SortOrder } from '@modules/common/types/sort-order';
import { GroupsListState } from '@modules/contacts/types/groups-list-state';
import { ContactsListState } from '@modules/contacts/types/contacts-list-state';
import { FilesListState } from '@modules/files/types/files-list-state';
import { FoldersListState } from '@modules/files/types/folders-list-state';
import { MessageFoldersListState } from '@modules/messages/types/message-folders-list-state';
import { MessagesListState } from '@modules/messages/types/messages-list-state';
import { NotebooksListState } from '@modules/notes/types/notebooks-list-state';
import { NotesListState } from '@modules/notes/types/notes-list-state';
import { type AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { type StateService } from '@modules/settings/services/state.service';
import { StateKey } from '@modules/settings/types/state-key';
import { ProjectsListState } from '@modules/tasks/types/projects-list-state';
import { TasksListState } from '@modules/tasks/types/tasks-list-state';
import { type ViewSettingsState, type ViewSortOption } from './view-settings';

/** A list a rail row can open: the app's items or its containers. */
export type ViewSettingsListId =
  | 'messages'
  | 'message-folders'
  | 'events'
  | 'calendars'
  | 'tasks'
  | 'projects'
  | 'notes'
  | 'notebooks'
  | 'contacts'
  | 'groups'
  | 'files'
  | 'folders';

export interface ViewSettingsListConfig {
  /** Where the app's list saves its sort and filters: one place per app list, whatever view is open. */
  readonly stateKey: StateKey;
  readonly noun: string;
  readonly sorts: (row: string) => readonly ViewSortOption[];
  readonly withUnread?: (row: string) => boolean;
  /** The list is an app's items, which have a Flagged rail row: that row is the flagged filter itself. */
  readonly hasFlaggedRow?: boolean;
  /** A state saved under the same key, for a list that is not on screen: the list restores it when it opens. */
  readonly detached: (deps: ViewSettingsDeps) => ViewSettingsState & WorkspaceListState;
}

/** A list state that keeps a copy of its sort and filters per workspace (every `BaseStitchListState`). */
export interface WorkspaceListState {
  useWorkspace(workspace: string | null): boolean;
}

export interface ViewSettingsDeps {
  readonly stateService: StateService;
  readonly searchService: AdvancedSearchService;
}

const asc = (key: string, label: string): ViewSortOption => ({ key, label, natural: SortOrder.ASC });
const desc = (key: string, label: string): ViewSortOption => ({ key, label, natural: SortOrder.DESC });

const TITLE_CREATED_UPDATED = [asc('title', 'Title'), desc('createdAt', 'Created date'), desc('updatedAt', 'Updated date')];

/** Mail's rows that list outgoing mail: they sort by To and never by From, and have no unread filter. */
const OUTGOING_ROWS = ['sent', 'draft', 'scheduled'];
const NO_TO_SORT_ROWS = ['bulk', 'spam', 'inbox', 'other_mail'];

const stateOf = (deps: ViewSettingsDeps, stateKey: StateKey) => ({
  stateKey,
  stateService: deps.stateService,
  searchService: deps.searchService,
});

export const VIEW_SETTINGS_LISTS: Readonly<Record<ViewSettingsListId, ViewSettingsListConfig>> = {
  messages: {
    stateKey: StateKey.messagesListState,
    noun: 'email',
    hasFlaggedRow: true,
    sorts: (row) => [
      asc('title', 'Subject'),
      desc('date', 'Date'),
      desc('attachmentsCount', 'Attachments'),
      ...(OUTGOING_ROWS.includes(row) ? [] : [asc('sentfrom', 'From')]),
      ...(NO_TO_SORT_ROWS.includes(row) ? [] : [asc('sentto', 'To')]),
    ],
    withUnread: (row) => !OUTGOING_ROWS.includes(row),
    detached: (deps) => new MessagesListState(stateOf(deps, StateKey.messagesListState)),
  },
  'message-folders': {
    stateKey: StateKey.messagesFoldersListState,
    noun: 'folder',
    sorts: () => TITLE_CREATED_UPDATED,
    detached: (deps) => new MessageFoldersListState(stateOf(deps, StateKey.messagesFoldersListState)),
  },
  events: {
    stateKey: StateKey.eventsListState,
    noun: 'event',
    hasFlaggedRow: true,
    sorts: () => [asc('title', 'Title'), asc('startTime', 'Start date'), asc('endTime', 'Due date')],
    detached: (deps) => new EventsListState(stateOf(deps, StateKey.eventsListState)),
  },
  calendars: {
    stateKey: StateKey.calendarsListState,
    noun: 'calendar',
    sorts: () => TITLE_CREATED_UPDATED,
    detached: (deps) => new CalendarsListState(stateOf(deps, StateKey.calendarsListState)),
  },
  tasks: {
    stateKey: StateKey.tasksListState,
    noun: 'task',
    hasFlaggedRow: true,
    sorts: () => [
      asc('title', 'Title'),
      asc('date', 'Start date'),
      asc('due-date', 'Due date'),
      desc('createdAt', 'Created date'),
      desc('priority', 'Priority'),
    ],
    detached: (deps) => new TasksListState(stateOf(deps, StateKey.tasksListState)),
  },
  projects: {
    stateKey: StateKey.projectsListState,
    noun: 'project',
    sorts: () => [
      asc('title', 'Title'),
      asc('date', 'Start date'),
      asc('due-date', 'Due date'),
      desc('createdAt', 'Created date'),
      desc('priority', 'Priority'),
    ],
    detached: (deps) => new ProjectsListState(stateOf(deps, StateKey.projectsListState)),
  },
  notes: {
    stateKey: StateKey.notesListState,
    noun: 'note',
    hasFlaggedRow: true,
    sorts: () => TITLE_CREATED_UPDATED,
    detached: (deps) => new NotesListState(stateOf(deps, StateKey.notesListState)),
  },
  notebooks: {
    stateKey: StateKey.notebooksListState,
    noun: 'notebook',
    sorts: () => TITLE_CREATED_UPDATED,
    detached: (deps) => new NotebooksListState(stateOf(deps, StateKey.notebooksListState)),
  },
  contacts: {
    stateKey: StateKey.contactsListState,
    noun: 'contact',
    hasFlaggedRow: true,
    sorts: () => TITLE_CREATED_UPDATED,
    detached: (deps) => new ContactsListState(stateOf(deps, StateKey.contactsListState)),
  },
  groups: {
    stateKey: StateKey.groupsListState,
    noun: 'group',
    sorts: () => TITLE_CREATED_UPDATED,
    detached: (deps) => new GroupsListState(stateOf(deps, StateKey.groupsListState)),
  },
  files: {
    stateKey: StateKey.filesListState,
    noun: 'file',
    hasFlaggedRow: true,
    sorts: () => [...TITLE_CREATED_UPDATED, desc('attachmentSize', 'Size')],
    detached: (deps) => new FilesListState(stateOf(deps, StateKey.filesListState)),
  },
  folders: {
    stateKey: StateKey.foldersListState,
    noun: 'folder',
    sorts: () => TITLE_CREATED_UPDATED,
    detached: (deps) => new FoldersListState(stateOf(deps, StateKey.foldersListState)),
  },
};

/** The live state service behind a list, if its list is on screen. */
export type LiveListState = ListStateService & { readonly state: ViewSettingsState };
